import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

export type WebsiteSectionStatus = 'idle' | 'generating' | 'error' | 'completed';

interface WebsiteSectionEditorProps {
  id: string;
  title: string;
  html: string;
  status?: WebsiteSectionStatus;
  disabled?: boolean;
  onChange: (newText: string) => void;
  onRegenerate?: () => void;
}

const WebsiteSectionEditor: React.FC<WebsiteSectionEditorProps> = ({
  id,
  title,
  html,
  status = 'completed',
  disabled = false,
  onChange,
  onRegenerate,
}) => {
  const [localText, setLocalText] = useState<string>(html || '');
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const editor = useEditor({
    extensions: [StarterKit],
    content: html || '',
    editable: !disabled && status !== 'generating',
    onUpdate: ({ editor }) => {
      // Persist plain text to align with backend SectionContent.body expectations
      const newText = editor.getText();
      setLocalText(newText);

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        onChange(newText);
      }, 600);
    },
  });

  // Keep editor content in sync when prop html changes externally
  useEffect(() => {
    const safe = html || '';
    if (editor && safe !== localText) {
      editor.commands.setContent(safe);
      setLocalText(safe);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html, editor]);

  // Update editable state when status/disabled changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled && status !== 'generating');
    }
  }, [disabled, status, editor]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      editor?.destroy();
    };
  }, [editor]);

  const statusPill = useMemo(() => {
    switch (status) {
      case 'generating':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-yellow-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Generating
          </span>
        );
      case 'error':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Error</span>;
      case 'completed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Completed</span>;
      default:
        return null;
    }
  }, [status]);

  if (!editor) return null;

  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <div>
          <div className="text-base sm:text-lg font-semibold text-gray-900">{title}</div>
          <div className="text-xs text-gray-500">Section ID: {id}</div>
        </div>
        <div className="flex items-center gap-2">
          {statusPill}
          {onRegenerate && status !== 'generating' && (
            <button
              type="button"
              onClick={onRegenerate}
              className="px-3 py-1.5 rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-200"
              title="Regenerate this section"
            >
              Regenerate
            </button>
          )}
        </div>
      </div>
      <div className="p-4">
        <div className="prose max-w-none">
          <EditorContent editor={editor} />
        </div>
        <div className="mt-2 text-xs text-gray-500 flex justify-end">
          {localText.length.toLocaleString()} chars
        </div>
      </div>
    </div>
  );
};

export default WebsiteSectionEditor;
